import { forwardRef, useImperativeHandle, useMemo } from "react";
import { Tag, Typography, message } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import copyIcon from "../../../../../assets/svg/copy.svg";
import CustomButton from "../../../../../components/CustomButton";
import { useIOSConfirm } from "../../../../../hooks";

import {
  type CustomerDTO,
  type HistoryOrderVO,
  ORDER_STATUS_DICT,
  OrderStatus,
  OrderAuditStatus,
  RiskAssessmentAuditStatus,
  getOrderDetail,
  createBasicOrder,
  createOrder,
  convertEmptyStringToNull,
  submitOrder,
  deleteOrder,
} from "@ifa/utils";
import styles from "./HistoryOrderSection.module.css";

const { Text } = Typography;

type HistoryOrderSectionProps = {
  customer: CustomerDTO | null;
  onRefresh?: () => void;
  fromCustomerList?: boolean; // 标记是否从客户列表进入
  customerId?: number; // 客户ID
};

export type HistoryOrderSectionHandle = Record<string, never>;

/**
 * 历史订单列表组件
 *
 * 导航来源追踪说明：
 * 所有从此组件跳转到其他页面时，都会在 navigate state 或 URL 参数中传递以下信息：
 * - from: 'customer-detail' 或 'customer-list' - 标识来源页面
 * - customerId: string - 客户 ID，用于返回时定位到正确的客户
 * - viewMode: 'history' - 标记返回后应该显示历史订单视图（仅 customer-list 时）
 *
 * 目标页面应该在返回时检查这些参数：
 * ```typescript
 * const location = useLocation();
 * const { from, customerId, viewMode } = location.state || {};
 *
 * const handleBack = () => {
 *   if (from === 'customer-detail' && customerId) {
 *     navigate(`/customers/${customerId}`);
 *   } else if (from === 'customer-list' && customerId) {
 *     navigate('/customers', { state: { selectedCustomerId: customerId, viewMode: viewMode } });
 *   } else {
 *     navigate('/orders'); // 默认返回订单列表
 *   }
 * };
 * ```
 */
export const HistoryOrderSection = forwardRef<HistoryOrderSectionHandle, HistoryOrderSectionProps>(({ customer, onRefresh, fromCustomerList, customerId }, ref) => {
  const navigate = useNavigate();
  const { confirm } = useIOSConfirm();

  // 从 customer.historyOrderList 获取历史订单列表
  const orderList = useMemo(() => {
    return customer?.historyOrderList || [];
  }, [customer?.historyOrderList]);

  useImperativeHandle(ref, () => ({}), []);

  const handleCopyOrderNo = (orderNo?: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!orderNo) return;
    navigator.clipboard.writeText(orderNo).then(() => {
      message.success("訂單號碼已複製");
    });
  };

  const handleReorder = async (orderId?: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!orderId) return;

    const hideLoading = message.loading("正在創建新訂單...", 0);
    try {
      const detail: any = await getOrderDetail(orderId);
      // Clean up data for new order
      const cleanData = {
        birthDate: detail.policyholder.basic.birthDate,
        chineseGivenName: detail.policyholder.basic.chineseGivenName,
        chineseSurname: detail.policyholder.basic.chineseSurname,
        englishGivenName: detail.policyholder.basic.englishGivenName,
        englishSurname: detail.policyholder.basic.englishSurname,
        forceAddOrder: detail.forceAddOrder,
        gender: detail.policyholder.basic.gender,
        idNumber: detail.policyholder.basic.idNumber,
        idType: detail.policyholder.basic.idType,
      };
      const payload = {
        ...cleanData,
        draft: true,
        submit: false,
      };

      // 根据来源设置不同的导航参数
      const fromParam = fromCustomerList ? "customer-list" : "customer-detail";
      const cid = customerId || customer?.customerId;

      if (detail.status === OrderStatus.Draft) {
        const res = await createBasicOrder(convertEmptyStringToNull(payload));
        if (res && res.orderId) {
          navigate(`/orders/${res.orderId}/edit?from=${fromParam}&customerId=${cid}&viewMode=history&initialStep=0`, {
            state: {
              orderStatus: OrderStatus.Draft,
            },
          });
        }
      } else {
        const createOrderPayload = {
          policyholder: detail.policyholder,
          insured: detail.insured,
          draft: true,
          submit: false,
        };
        if (
          createOrderPayload.policyholder &&
          detail.policyholder?.sourceId != null
        ) {
          createOrderPayload.policyholder.policyholderId =
            detail.policyholder.sourceId;
        }
        if (
          createOrderPayload.insured &&
          detail.insured?.sourceId != null
        ) {
          createOrderPayload.insured.insuredId = detail.insured.sourceId;
        }

        const res = await createOrder(convertEmptyStringToNull(createOrderPayload));
        if (res) {
          navigate(`/orders/${res}/edit?from=${fromParam}&customerId=${cid}&viewMode=history&initialStep=0`, {
            state: {
              orderStatus: OrderStatus.Draft,
            },
          });
        }
      }
    } catch (error) {
      console.error("Re-insure/Copy failed:", error);
      message.error("創建新訂單失敗");
    } finally {
      hideLoading();
    }
  };

  const handleViewDetail = (order: HistoryOrderVO) => {
    const orderId = order.orderId;
    if (!orderId) return;

    // 根据来源设置不同的导航参数
    const fromParam = fromCustomerList ? "customer-list" : "customer-detail";
    const cid = customerId || customer?.customerId;

    // If order is completed, navigate to read-only detail page
    // Otherwise navigate to edit page
    if (order.orderStatus !== OrderStatus.Draft && order.orderStatus !== OrderStatus.PendingConfirmation && order.orderStatus !== OrderStatus.PendingSignature) {
      navigate(`/orders/${orderId}?from=${fromParam}&customerId=${cid}&viewMode=history`, {
        state: {
          orderStatus: order.orderStatus,
          orderAuditStatus: order.orderAuditStatus,
        },
      });
    } else {
      // If it is a draft, force start at step 0
      navigate(`/orders/${orderId}/edit?from=${fromParam}&customerId=${customer?.customerId}&initialStep=0`, {
        state: {
          orderStatus: order.orderStatus,
          orderAuditStatus: order.orderAuditStatus,
        },
      });
    }
  };

  const handleSubmitDraftOrder = (orderId?: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!orderId) return;

    confirm({
      title: "確認提交",
      content: "確認要提交此訂單嗎？",
      okText: "確認",
      cancelText: "取消",
      onOk: async () => {
        try {
          await submitOrder(orderId);
          message.success("訂單提交成功");
          // Refresh customer data
          onRefresh?.();
        } catch (error: any) {
          console.error("Submit failed:", error);
          if (error?.message) {
            message.error(error.message);
          } else {
            message.error("提交失敗");
          }
        }
      },
    });
  };

  const handleDeleteOrder = (orderId?: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!orderId) return;

    confirm({
      title: "確認刪除",
      content: "確定要刪除此訂單嗎？此操作無法恢復。",
      okText: "確認刪除",
      cancelText: "取消",
      onOk: async () => {
        try {
          await deleteOrder(orderId);
          message.success("訂單已刪除");
          // Refresh customer data
          onRefresh?.();
        } catch (error) {
          console.error("Delete failed:", error);
          // Global error handler will show error message
        }
      },
    });
  };

  const handleRiskQuestionnaire = (orderId?: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!orderId) return;
    navigate(`/orders/${orderId}/risk?from=${fromParam}&customerId=${customer?.customerId}`);
  };

  const handleCorrectionOrder = (orderId?: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!orderId) return;
    navigate(`/orders/${orderId}/correction?from=${fromParam}&customerId=${customer?.customerId}`);
  };

  // 获取订单状态文本
  const getStatusText = (status: string) => {
    return ORDER_STATUS_DICT[status as OrderStatus] || status;
  };

  // 渲染操作按钮（根据订单状态显示不同按钮）
  const renderActionButtons = (order: HistoryOrderVO) => {
    const orderId = order.orderId || 0;
    const status = order.orderStatus;
    const buttons = [];

    // Common: Re-insure (Hidden when Pending Modification or Pending Confirmation)
    if (status !== OrderStatus.Draft && order.orderAuditStatus !== OrderAuditStatus.PendingModification && order.orderAuditStatus !== OrderAuditStatus.PendingConfirmation) {
      buttons.push(
        <CustomButton
          key="reinsure"
          height={36}
          fontSize={14}
          bgColor="transparent"
          color="var(--theme-primary)"
          style={{ border: "1px solid var(--theme-primary)", padding: "0 12px" }}
          className="re-insure-btn"
          onClick={(e: React.MouseEvent) => handleReorder(orderId, e)}
        >
          再次投保
        </CustomButton>,
      );
    }

    // Specific Buttons based on Status
    switch (status) {
      case OrderStatus.Draft:
        // Submit (Edit)
        buttons.push(
          <CustomButton key="submit" height={36} fontSize={14} bgColor="var(--theme-primary)" color="#fff" style={{ padding: "0 12px" }} onClick={(e: React.MouseEvent) => handleSubmitDraftOrder(orderId, e)}>
            提交訂單
          </CustomButton>,
        );
        // Delete
        buttons.unshift(
          <CustomButton
            key="delete"
            height={36}
            fontSize={14}
            bgColor="transparent"
            color="#ff4d4f"
            style={{ border: "1px solid #ff4d4f", padding: "0 12px" }}
            onClick={(e: React.MouseEvent) => handleDeleteOrder(orderId, e)}
          >
            刪除
          </CustomButton>,
        );
        break;

      case OrderStatus.OrderAudit:
        if (order.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.PendingSubmission) {
          buttons.push(
            <CustomButton key="risk" height={36} fontSize={14} bgColor="var(--theme-primary)" color="#fff" style={{ padding: "0 12px" }} onClick={(e: React.MouseEvent) => handleRiskQuestionnaire(orderId, e)}>
              風險評估問卷
            </CustomButton>,
          );
        }

        if (order.orderAuditStatus === OrderAuditStatus.PendingModification) {
          buttons.push(
            <CustomButton key="modify" height={36} fontSize={14} bgColor="var(--theme-primary)" color="#fff" style={{ padding: "0 12px" }} onClick={(e: React.MouseEvent) => handleCorrectionOrder(orderId, e)}>
              修改訂單
            </CustomButton>,
          );
        }
        break;

      case OrderStatus.Expired:
        buttons.unshift(
          <CustomButton
            key="delete"
            height={36}
            fontSize={14}
            bgColor="transparent"
            color="#ff4d4f"
            style={{ border: "1px solid #ff4d4f", padding: "0 12px" }}
            onClick={(e: React.MouseEvent) => handleDeleteOrder(orderId, e)}
          >
            刪除
          </CustomButton>,
        );
        break;
    }

    return buttons;
  };

  return (
    <div className={styles.container}>
      {orderList.length === 0 ? (
        <div className={styles.emptyContainer}>
          <div className={styles.emptyContent}>
            <div className={styles.emptyIcon}>
              <FileTextOutlined />
            </div>
            <div className={styles.emptyTitle}>暫無歷史訂單</div>
            <div className={styles.emptyDescription}>還沒有任何歷史訂單記錄</div>
          </div>
        </div>
      ) : (
        <div className={styles.ordersGrid}>
          {orderList.map((order) => (
            <div key={order.orderId} className={styles.orderCard} onClick={() => handleViewDetail(order)}>
              <div className={styles.orderCardContent}>
                {/* Top Section: Order Number + Copy + Status Tag */}
                <div className={styles.orderTopSection}>
                  <div className={styles.orderTopLeft}>
                    <div className={styles.orderNumber}>{order.orderNo}</div>
                    <CustomButton
                      width={24}
                      height={20}
                      bgColor="transparent"
                      color="#999"
                      icon={<img src={copyIcon} alt="copy" style={{ width: 16, height: 16 }} />}
                      className={styles.copyOrderNumberBtn}
                      onClick={(e: React.MouseEvent) => handleCopyOrderNo(order.orderNo, e)}
                    />
                  </div>
                  <Tag className={`${styles.orderStatusTag} ${styles[`status${order.orderStatus?.replace(/_/g, "")}`] || ""}`}>{getStatusText(order.orderStatus || "")}</Tag>
                </div>

                {/* Middle Section: Product + 3-Col Info */}
                <div className={styles.orderMiddleSection}>
                  {/* Product Name */}
                  <div className={`${styles.orderRowProduct} mt`}>
                    <div className={styles.productName}>
                      {order.productName || "未選擇產品"}
                      {order.contributionYears ? `(${order.contributionYears}年)` : ""}
                    </div>
                  </div>

                  {/* 3 Columns Info */}
                  <div className={styles.orderInfoGrid}>
                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>客戶</div>
                      <div className={styles.infoValue}>{order.policyholderName || "N/A"}</div>
                    </div>

                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>首期保費</div>
                      <div className={`${styles.infoValue} ${styles.premiumValue}`}>
                        {order.premiumPerPeriod ? order.premiumPerPeriod.toLocaleString() : "--"}
                        {order.currency && <span className={styles.currencySuffix}>{order.currency}</span>}
                      </div>
                    </div>

                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>提交日期</div>
                      <div className={styles.infoValue}>{order.createTime ? dayjs(order.createTime).format("YYYY-MM-DD") : "N/A"}</div>
                    </div>
                  </div>
                </div>

                {/* Bottom Section: Action Buttons */}
                <div className={styles.orderBottomSection}>
                  <Text type="secondary" className={styles.orderDateText}></Text>
                  <div style={{ display: "flex", gap: 12 }}>{renderActionButtons(order)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
